import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

api.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("bb_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

/**
 * Fetch a protected upload (e.g. cleaner insurance proof) using the admin
 * JWT via Authorization header, wrap the bytes in a Blob URL, and open it in
 * a new tab. Never exposes the token in the URL/query string and never makes
 * the underlying storage path publicly accessible.
 *
 * `pathOrUrl` is the value stored on the record (e.g. cleaner_insurance.document_url).
 * Historically this is the raw storage path returned by POST /api/upload
 * (e.g. "bbcleaners/uploads/<uid>/<uuid>.pdf"), so we normalise both forms.
 */
export async function openProtectedFile(pathOrUrl) {
  if (!pathOrUrl) throw new Error("No document path");
  let path = String(pathOrUrl).trim();
  // If a full URL slipped in, keep only the pathname portion.
  if (/^https?:\/\//i.test(path)) {
    try { path = new URL(path).pathname; } catch { /* noop */ }
  }
  // Strip leading slashes and an optional /api/files/ prefix.
  path = path.replace(/^\/+/, "").replace(/^api\/files\//, "");
  const encoded = path.split("/").map(encodeURIComponent).join("/");
  const res = await api.get(`/files/${encoded}`, { responseType: "blob" });
  const ct = res.headers?.["content-type"] || res.data?.type || "application/octet-stream";
  const blob = new Blob([res.data], { type: ct });
  const objUrl = URL.createObjectURL(blob);
  window.open(objUrl, "_blank", "noopener,noreferrer");
  // Give the new tab time to load before revoking.
  setTimeout(() => URL.revokeObjectURL(objUrl), 60_000);
}

export const CURRENCIES = {
  USD: { symbol: "$", rate: 1.0 },
  EUR: { symbol: "€", rate: 0.92 },
  GBP: { symbol: "£", rate: 0.79 },
  CAD: { symbol: "C$", rate: 1.36 },
  AUD: { symbol: "A$", rate: 1.52 },
  INR: { symbol: "₹", rate: 83.2 },
};

export const LANGUAGES = {
  en: { label: "English", flag: "US" },
  es: { label: "Español", flag: "ES" },
  fr: { label: "Français", flag: "FR" },
  de: { label: "Deutsch", flag: "DE" },
  it: { label: "Italiano", flag: "IT" },
};

export const TRANSLATIONS = {
  en: {
    hero_title: "Professional Cleaning & Care Services,",
    hero_title_2: "Delivered to You",
    hero_sub: "Book trusted CLEANBAZ Pros for home, commercial, car, pet care, outdoor, and everyday convenience services.",
    get_quote: "Get Instant Quote",
    become_cleaner: "Become a Cleaner",
    pricing_title: "Live Pricing Estimator",
    pricing_sub: "Adjust rooms, add extras, see your total in real time.",
    book_now: "Book & Pay",
    login: "Login",
    signup: "Sign Up",
  },
  es: {
    hero_title: "Hogares Impecables,",
    hero_title_2: "Entregados en Todo el País",
    hero_sub: "Coincidencias instantáneas con limpiadores locales CLEANBAZ verificados. Pagos protegidos por depósito. Servicio en todo Estados Unidos.",
    get_quote: "Cotización Instantánea",
    become_cleaner: "Únete como Limpiador",
    pricing_title: "Estimador de Precios en Vivo",
    pricing_sub: "Ajusta habitaciones, agrega extras, mira tu total en tiempo real.",
    book_now: "Reservar y Pagar",
    login: "Iniciar Sesión",
    signup: "Registrarse",
  },
  fr: {
    hero_title: "Maisons Étincelantes,",
    hero_title_2: "Livrées à l'Échelle Nationale",
    hero_sub: "Correspondances instantanées avec des CLEANBAZ Pros locaux vérifiés. Paiements protégés par séquestre. Service disponible aux États-Unis.",
    get_quote: "Devis Instantané",
    become_cleaner: "Devenir Nettoyeur",
    pricing_title: "Estimateur de Prix en Direct",
    pricing_sub: "Ajustez les chambres, ajoutez des extras, voyez votre total en temps réel.",
    book_now: "Réserver et Payer",
    login: "Connexion",
    signup: "S'inscrire",
  },
  de: {
    hero_title: "Glänzende Häuser,",
    hero_title_2: "Landesweit Geliefert",
    hero_sub: "Sofortige Übereinstimmungen mit geprüften lokalen CLEANBAZ Pros. Treuhand-geschützte Zahlungen. Service in den gesamten USA.",
    get_quote: "Sofortangebot",
    become_cleaner: "Reiniger Werden",
    pricing_title: "Live-Preisschätzer",
    pricing_sub: "Passen Sie Zimmer an, fügen Sie Extras hinzu, sehen Sie Ihre Summe in Echtzeit.",
    book_now: "Buchen & Bezahlen",
    login: "Anmelden",
    signup: "Registrieren",
  },
  it: {
    hero_title: "Case Scintillanti,",
    hero_title_2: "Consegnate in Tutto il Paese",
    hero_sub: "Abbinamenti istantanei con CLEANBAZ Pro locali verificati. Pagamenti protetti da deposito a garanzia. Servizio in tutti gli Stati Uniti.",
    get_quote: "Preventivo Istantaneo",
    become_cleaner: "Diventa un Addetto",
    pricing_title: "Stimatore di Prezzi in Diretta",
    pricing_sub: "Regola le stanze, aggiungi extra, guarda il totale in tempo reale.",
    book_now: "Prenota e Paga",
    login: "Accedi",
    signup: "Iscriviti",
  },
};

export function formatPrice(usd, currency) {
  const c = CURRENCIES[currency] || CURRENCIES.USD;
  const val = usd * c.rate;
  return `${c.symbol}${val.toFixed(2)}`;
}
